import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertCircle,
  ArrowLeft,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  Loader2,
  MessageCircle,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { cn } from '@/lib/utils';
import type { WhatsAppConnectRequest, WhatsAppConnectResponse } from '@/types/api';

interface WhatsAppConfigFormProps {
  onSubmit: (data: WhatsAppConnectRequest) => Promise<WhatsAppConnectResponse | undefined>;
  isLoading?: boolean;
  onCancel: () => void;
}

const WHATSAPP_ACCENT = {
  border: 'border-channel-whatsapp/20',
  iconBg: 'bg-channel-whatsapp/10',
  iconText: 'text-channel-whatsapp',
  button: 'bg-channel-whatsapp hover:bg-channel-whatsapp/90 text-channel-whatsapp-foreground',
  softBg: 'bg-channel-whatsapp/5',
  softBorder: 'border-channel-whatsapp/15',
};

export const WhatsAppConfigForm: React.FC<WhatsAppConfigFormProps> = ({
  onSubmit,
  isLoading = false,
  onCancel,
}) => {
  const [formData, setFormData] = useState<WhatsAppConnectRequest>({
    businessAccountId: '',
    phoneNumberId: '',
    accessToken: '',
    businessName: '',
    phoneNumber: '',
  });
  const [showToken, setShowToken] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleInputChange = (field: keyof WhatsAppConnectRequest, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const requiredFields = ['businessAccountId', 'phoneNumberId', 'accessToken', 'businessName'];
    const missingFields = requiredFields.filter((field) => !formData[field as keyof WhatsAppConnectRequest]);

    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.join(', ')}`);
      return;
    }

    try {
      const result = await onSubmit(formData);
      if (result?.webhookUrl) {
        setWebhookUrl(result.webhookUrl);
        setIsSuccess(true);
      }
    } catch (error) {
      console.error('WhatsApp connection failed:', error);
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast.success('Webhook URL copied to clipboard');
  };

  const resetForm = () => {
    setFormData({
      businessAccountId: '',
      phoneNumberId: '',
      accessToken: '',
      businessName: '',
      phoneNumber: '',
    });
    setIsSuccess(false);
    setWebhookUrl('');
  };

  if (isSuccess && webhookUrl) {
    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
        <Card className="max-w-none border-success/25 bg-success/5 dark:bg-success/10">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2.5">
              <motion.span
                initial={{ scale: 0, rotate: -45 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 22 }}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-success text-success-foreground"
              >
                <MessageCircle className="h-4 w-4" />
              </motion.span>
              <CardTitle className="text-base text-success">WhatsApp connected</CardTitle>
            </div>
            <CardDescription className="text-success/80">
              Complete setup by adding the webhook URL below to your WhatsApp Business Manager.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-0">
            <div>
              <Label className="text-xs font-medium text-success">Webhook URL</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input value={webhookUrl} readOnly className="h-9 border-success/25 bg-white dark:bg-slate-900 text-xs" />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyWebhookUrl}
                  className="h-9 w-9 shrink-0 border-success/25 hover:bg-success/10"
                  aria-label="Copy webhook URL"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="rounded-lg border border-success/20 dark:border-success/30 bg-white dark:bg-slate-900 p-3.5">
              <h4 className="text-sm font-medium text-card-foreground dark:text-white">Next steps</h4>
              <ol className="mt-1.5 list-decimal space-y-1 pl-4 text-xs text-muted-foreground dark:text-slate-400">
                <li>Copy the webhook URL above</li>
                <li>Go to your WhatsApp Business Manager</li>
                <li>Navigate to App Settings → Webhooks</li>
                <li>Add the webhook URL and verify</li>
                <li>Subscribe to message events</li>
              </ol>
            </div>

            <div className="flex flex-wrap gap-2.5">
              <Button
                onClick={() => window.open('https://business.facebook.com/', '_blank')}
                className={cn('h-9 text-xs', WHATSAPP_ACCENT.button)}
              >
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                Open WhatsApp Manager
              </Button>
              <Button variant="outline" size="sm" onClick={resetForm} className="h-9 text-xs border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-850 text-slate-700 dark:text-slate-200">
                Connect another account
              </Button>
              <Button variant="ghost" size="sm" onClick={onCancel} className="h-9 text-xs">
                Done
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
      <Card className={cn('max-w-none border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900', WHATSAPP_ACCENT.border)}>
        <CardHeader className="pb-3">
          <button
            type="button"
            onClick={onCancel}
            className="mb-1 flex w-fit items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-card-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to integrations
          </button>
          <div className="flex items-center gap-2.5">
            <span className={cn('flex h-9 w-9 items-center justify-center rounded-lg', WHATSAPP_ACCENT.iconBg)}>
              <MessageCircle className={cn('h-4.5 w-4.5', WHATSAPP_ACCENT.iconText)} />
            </span>
            <div>
              <CardTitle className="text-base">Connect WhatsApp Business</CardTitle>
              <CardDescription className="mt-0.5">
                Enter your WhatsApp Business API credentials to enable automated messaging.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="businessAccountId" className="text-xs font-medium">
                  Business Account ID *
                </Label>
                <Input
                  id="businessAccountId"
                  type="text"
                  value={formData.businessAccountId}
                  onChange={(e) => handleInputChange('businessAccountId', e.target.value)}
                  placeholder="123456789012345"
                  className="mt-1 h-9 text-sm"
                  required
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Business Settings → Business Info
                </p>
              </div>

              <div>
                <Label htmlFor="phoneNumberId" className="text-xs font-medium">
                  Phone Number ID *
                </Label>
                <Input
                  id="phoneNumberId"
                  type="text"
                  value={formData.phoneNumberId}
                  onChange={(e) => handleInputChange('phoneNumberId', e.target.value)}
                  placeholder="987654321098765"
                  className="mt-1 h-9 text-sm"
                  required
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Business Manager → Phone Numbers
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="accessToken" className="text-xs font-medium">
                Permanent Access Token *
              </Label>
              <div className="relative mt-1">
                <Input
                  id="accessToken"
                  type={showToken ? 'text' : 'password'}
                  value={formData.accessToken}
                  onChange={(e) => handleInputChange('accessToken', e.target.value)}
                  placeholder="EAAxxxxxxxxxx..."
                  className="h-9 pr-9 text-sm"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0.5 top-0.5 h-8 w-8"
                  onClick={() => setShowToken(!showToken)}
                  aria-label={showToken ? 'Hide token' : 'Show token'}
                >
                  {showToken ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </Button>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                System Users → Create Token (permanent, not temporary)
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="businessName" className="text-xs font-medium">
                  Business Name *
                </Label>
                <Input
                  id="businessName"
                  type="text"
                  value={formData.businessName}
                  onChange={(e) => handleInputChange('businessName', e.target.value)}
                  placeholder="Your Business Name"
                  className="mt-1 h-9 text-sm"
                  required
                />
              </div>

              <div>
                <Label htmlFor="phoneNumber" className="text-xs font-medium">
                  Phone Number
                </Label>
                <Input
                  id="phoneNumber"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  placeholder="+1234567890"
                  className="mt-1 h-9 text-sm"
                />
              </div>
            </div>

            <div className={cn('rounded-lg border p-3.5', WHATSAPP_ACCENT.softBg, WHATSAPP_ACCENT.softBorder)}>
              <div className="flex items-start gap-2">
                <AlertCircle className={cn('mt-0.5 h-4 w-4 shrink-0', WHATSAPP_ACCENT.iconText)} />
                <div>
                  <h4 className="text-xs font-medium text-card-foreground">Required permissions</h4>
                  <ul className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
                    <li>whatsapp_business_management</li>
                    <li>whatsapp_business_messaging</li>
                    <li>business_management (read)</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-2.5 pt-1">
              <Button type="submit" disabled={isLoading} className={cn('h-9 text-sm', WHATSAPP_ACCENT.button)}>
                {isLoading ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <MessageCircle className="mr-1.5 h-4 w-4" />
                )}
                Connect WhatsApp Business
              </Button>
              <Button type="button" variant="outline" size="default" onClick={onCancel} className="h-9 text-sm">
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default WhatsAppConfigForm;
